import { IconCheck } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { DiscountGradientIcon } from "@/components/common/gradients/discount-gradient-icon";
import { CardHeader } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type {
  DiscountProgressMessageVariant,
  DiscountProgressType,
  DiscountTierData,
} from "./use-discount-cart-progress";

type DiscountCartProgressHeaderProps = {
  currentDiscountAmountLabel: string;
  currentDiscountLabel: string;
  discountType: DiscountProgressType;
  displayOnlyHeader: boolean;
  messageVariant: DiscountProgressMessageVariant;
  nextTier: DiscountTierData | null;
  shouldShowAlreadyHaveDiscount: boolean;
};

const renderMessage = ({
  currentDiscountAmountLabel,
  currentDiscountLabel,
  messageVariant,
  nextTier,
  t,
}: {
  currentDiscountAmountLabel: string;
  currentDiscountLabel: string;
  messageVariant: DiscountProgressMessageVariant;
  nextTier: DiscountTierData | null;
  t: ReturnType<typeof useTranslations>;
}) => {
  if (messageVariant === "gratz") {
    return t.rich(`discounts.discountMessage.gratz`, {
      discount: currentDiscountAmountLabel,
      strong: (chunks) => <strong className="font-semibold">{chunks}</strong>,
    });
  }

  if (messageVariant === "nextTierMax") {
    return t.rich(`discounts.discountMessage.addDaysToNextTierMax`, {
      days: nextTier?.days ?? 0,
      discount: nextTier?.discountAmountLabel ?? currentDiscountLabel,
      strong: (chunks) => <strong className="font-semibold">{chunks}</strong>,
    });
  }

  return t.rich(`discounts.discountMessage.addDaysToNextTier`, {
    days: nextTier?.days ?? 0,
    discount: nextTier?.discountAmountLabel ?? currentDiscountLabel,
    tier: t(`discounts.discountTiers.tier${nextTier?.tierIndex ?? 1}`),
    strong: (chunks) => <strong className="font-semibold">{chunks}</strong>,
  });
};

export const DiscountCartProgressHeader = ({
  currentDiscountAmountLabel,
  currentDiscountLabel,
  discountType,
  displayOnlyHeader,
  messageVariant,
  nextTier,
  shouldShowAlreadyHaveDiscount,
}: DiscountCartProgressHeaderProps) => {
  const t = useTranslations();

  return (
    <div
      className={cn(
        "rounded-lg p-px",
        displayOnlyHeader && "bg-(image:--discount-gradient-icon)",
      )}
    >
      <CardHeader
        className={cn(
          "flex-row items-center gap-2 rounded-t-lg bg-red-50 py-3 pr-2 pl-4",
          displayOnlyHeader && "rounded-lg",
        )}
      >
        <DiscountGradientIcon className="mb-0" />
        <p className="font-medium">
          {renderMessage({
            currentDiscountAmountLabel,
            currentDiscountLabel,
            messageVariant,
            nextTier,
            t,
          })}
        </p>
      </CardHeader>

      {shouldShowAlreadyHaveDiscount && (
        <p className="flex items-center gap-2 bg-brand-green-300 px-4 py-1.5 text-center font-medium text-[15px] text-brand-green-400">
          <IconCheck className="h-6 w-6" />
          {t(
            discountType === "percentage"
              ? "discounts.discountMessagePercentage.alreadyHaveDiscount"
              : "discounts.discountMessage.alreadyHaveDiscount",
            {
              discount: currentDiscountLabel,
            },
          )}
        </p>
      )}
    </div>
  );
};
